import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { Nav } from "@/components/marketing/Nav";
import { CtaFooter } from "@/components/marketing/CtaFooter";
import { Crest } from "@/components/Crest";
import { EcgLine } from "@/components/EcgLine";

const ENQUIRY_TYPES = [
  "Private GP / Independent Healthcare",
  "NHS Primary Care / PCN",
  "Clinical Research / Research Echo",
  "Specialist Referral Query",
  "Other",
];

const inputCls =
  "w-full bg-white border border-navy/15 px-4 py-3 text-sm text-navy focus:outline-none focus:border-gold transition-colors";
const labelCls = "font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 block mb-2";

const initial = { name: "", organization: "", email: "", phone: "", enquiry_type: ENQUIRY_TYPES[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/contact", form);
      setDone(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-ivory min-h-screen" data-testid="contact-page">
      <Nav />

      <div className="pt-32 pb-24 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 bg-navy p-10 lg:p-14 flex flex-col justify-between min-h-[560px]"
        >
          <div>
            <Crest light className="h-11 mb-12" />
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-gold mb-4">
              Contact CVI
            </p>
            <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-ivory leading-tight mb-8">
              Discuss a diagnostic service.
            </h1>
            <p className="text-sm text-ivory/60 leading-relaxed max-w-sm">
              Whether you run a private practice, a Primary Care Network or a research
              programme, tell us what you need and our clinical team will respond.
            </p>
          </div>

          <div className="mt-12">
            <div className="border-t border-ivory/10 pt-8 space-y-5">
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ivory/40 mb-1">Call Us</p>
                <a
                  href="tel:+447590469090"
                  data-testid="contact-phone-link"
                  className="text-sm text-gold hover:text-champagne transition-colors"
                >
                  07590 469090
                </a>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ivory/40 mb-1">Email Us</p>
                <a
                  href="mailto:navazhjalaludeen@nhs.net"
                  data-testid="contact-email-link"
                  className="text-sm text-gold hover:text-champagne transition-colors break-all"
                >
                  navazhjalaludeen@nhs.net
                </a>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ivory/40 mb-1">Enquiries</p>
                <p className="text-sm text-ivory" data-testid="contact-email-display">
                  Use the form — messages go directly to the CVI clinical team
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ivory/40 mb-1">Response Time</p>
                <p className="text-sm text-ivory">Within one working day</p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-ivory/40 mb-1">Specialists</p>
                <p className="text-sm text-ivory/70">
                  For case referrals, please use the{" "}
                  <Link to="/login" data-testid="contact-portal-link" className="text-gold hover:text-champagne transition-colors">
                    specialist portal
                  </Link>
                  .
                </p>
              </div>
            </div>
            <EcgLine className="text-gold/60 h-10 w-56 mt-10" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 paper-grain"
        >
          {done ? (
            <div className="border border-gold/50 bg-white p-10 lg:p-14 h-full flex flex-col justify-center" data-testid="contact-success">
              <Crest className="h-12 mb-8" />
              <h2 className="font-serif text-3xl font-semibold text-navy mb-4">Enquiry received.</h2>
              <p className="text-sm text-navy/60 leading-relaxed max-w-md mb-8">
                Thank you — the CVI clinical team will review your message and respond
                within one working day.
              </p>
              <Link
                to="/"
                data-testid="contact-success-home-link"
                className="inline-block w-fit bg-navy text-ivory font-mono text-xs tracking-[0.25em] uppercase px-8 py-4 hover:bg-gold hover:text-navy transition-colors"
              >
                Return Home
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-6 lg:pt-4" data-testid="contact-form">
              <div>
                <label className={labelCls}>Name</label>
                <input required value={form.name} onChange={set("name")} data-testid="contact-name-input" className={inputCls} placeholder="Dr Jane Smith" />
              </div>
              <div>
                <label className={labelCls}>Organisation</label>
                <input value={form.organization} onChange={set("organization")} data-testid="contact-organization-input" className={inputCls} placeholder="Practice / PCN / Research unit" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input type="email" required value={form.email} onChange={set("email")} data-testid="contact-email-input" className={inputCls} placeholder="you@organisation.nhs.uk" />
              </div>
              <div>
                <label className={labelCls}>Phone (optional)</label>
                <input type="tel" value={form.phone} onChange={set("phone")} data-testid="contact-phone-input" className={inputCls} placeholder="07…" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Service Interest</label>
                <select value={form.enquiry_type} onChange={set("enquiry_type")} data-testid="contact-type-select" className={inputCls}>
                  {ENQUIRY_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Message</label>
                <textarea rows={6} required value={form.message} onChange={set("message")} data-testid="contact-message-input" className={inputCls} placeholder="Tell us about your requirements, patient volumes or study protocol…" />
              </div>

              {error && (
                <p data-testid="contact-error" className="sm:col-span-2 text-sm text-crimson border border-crimson/30 bg-crimson/5 px-4 py-3">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="sm:col-span-2 w-full bg-navy text-ivory font-mono text-xs tracking-[0.25em] uppercase py-4 hover:bg-gold hover:text-navy transition-colors disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          )}
        </motion.div>
      </div>

      <CtaFooter />
    </div>
  );
}
